import { MutableRefObject, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import {
  TEMPLATE_FIELD_INLINE_NAME,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_DROPDOWN_NAME,
} from '../doc-engine/templateFields';
import captureSelectionSnapshot from '../helpers/captureSelectionSnapshot.helper';
import { findBlockFieldWrap } from '../helpers/templateFieldValidation.helper';
import { NEW_DROPDOWN_OPTIONS, normalizeDropdownOption } from '../tiptap/nodes/templateField/dropdownFieldUtils';
import findNodeByFieldId from '../tiptap/utils/findNodeByFieldId';
import selectNodeByFieldId from '../tiptap/utils/selectNodeByFieldId';

/**
 * Select all content inside an inline/block field so the user sees what was wrapped.
 * Called after field creation - visual feedback showing the field's scope.
 */
const selectFieldContent = (editor: Editor, fieldId: string) => {
  const found = findNodeByFieldId(editor, fieldId);
  if (!found) return;
  const { doc } = editor.state;
  const $start = doc.resolve(found.pos + 1);
  const $end = doc.resolve(found.pos + found.node.nodeSize - 1);
  const sel = TextSelection.between($start, $end);
  editor.view.dispatch(editor.state.tr.setSelection(sel).scrollIntoView());
};

const useTemplateFieldInsert = (
  editor: Editor | null,
  templateDropdownOptionsRef: MutableRefObject<Record<string, string[]>>
) => {
  const handleInsertField = useCallback((isInline: boolean) => {
    if (!editor) return;
    const fieldId = uuidv4();
    const snapshot = captureSelectionSnapshot(editor);

    if (snapshot) {
      const tag = isInline ? TEMPLATE_FIELD_INLINE_NAME : TEMPLATE_FIELD_BLOCK_NAME;
      const fieldType = editor.schema.nodes[tag];
      if (!fieldType) return;

      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          if (isInline) {
            // Inline: cut from parent content (not tr.selection) to preserve marks on the slice edges
            // that ProseMirror's replace would otherwise strip.
            const { from, to } = tr.selection;
            const $from = tr.doc.resolve(from);
            const content = $from.parent.content.cut(from - $from.start(), to - $from.start());
            tr.replaceWith(from, to, fieldType.create({ fieldId }, content));
          } else {
            // Block: tr.wrap, not an HTML round-trip, which corrupts open slices like nested lists.
            const target = findBlockFieldWrap(tr.doc, tr.selection, fieldType, { fieldId });
            if (!target) return false;
            tr.wrap(target.range, target.wrapping);
          }
          return true;
        })
        .run();
      selectFieldContent(editor, fieldId);
      return;
    }

    // No selection - insert with placeholder (existing behavior)
    editor
      .chain()
      .focus()
      .insertContent({
        type: isInline ? TEMPLATE_FIELD_INLINE_NAME : TEMPLATE_FIELD_BLOCK_NAME,
        attrs: { fieldId },
        content: isInline
          ? [{ type: 'text', text: 'Placeholder' }]
          : [{ type: 'paragraph', content: [{ type: 'text', text: 'Placeholder' }] }],
      })
      .run();

    // Select "Placeholder" so the user can type to replace immediately.
    selectFieldContent(editor, fieldId);
  }, []);

  const handleInsertDropdownField = useCallback(() => {
    if (!editor) return;
    const fieldId = uuidv4();
    const snapshot = captureSelectionSnapshot(editor);

    if (snapshot) {
      const normalized = normalizeDropdownOption(snapshot.text);
      if (normalized) {
        // Selection exists - use selected text as the first (and only) option
        templateDropdownOptionsRef.current[fieldId] = [normalized];
        editor
          .chain()
          .focus()
          .insertContent({
            type: TEMPLATE_FIELD_DROPDOWN_NAME,
            attrs: { fieldId, selectedValue: normalized },
          })
          .run();
        selectNodeByFieldId(editor, fieldId);
        return;
      }
    }

    // No selection or empty after normalization - default behavior
    templateDropdownOptionsRef.current[fieldId] = [...NEW_DROPDOWN_OPTIONS];
    editor
      .chain()
      .focus()
      .insertContent({
        type: TEMPLATE_FIELD_DROPDOWN_NAME,
        attrs: { fieldId },
      })
      .run();
    selectNodeByFieldId(editor, fieldId);
  }, [templateDropdownOptionsRef]);

  return { handleInsertField, handleInsertDropdownField };
};

export default useTemplateFieldInsert;
